import type { ReceiptTypeName } from '../transaction-phoenix/api';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  getPhoenixApiTenantUrl,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  PostServiceProviderDto,
  ServiceProviderListDto,
  ServiceProviderWithStatementDescriptorDto,
} from '@pflegenavi/shared/api';
import { ServiceProviderStatusCombined } from '@pflegenavi/shared/api';
import { PaginatedResultSet } from '@pflegenavi/shared/utils';
import { Filter } from '../filter';

export interface ServiceProviderFilters {
  /** Case-insensitive match on the service provider name. */
  name?: string;
  receiptTypeId?: string;
  /** Omitted for all statuses, archived ones included. */
  combinedStatus?: ServiceProviderStatusCombined;
}

export type ServiceProviderSortColumn = 'name' | 'service_provider_email';

export interface ListServiceProvidersParams {
  nursingHomeId: string;
  /** 0-indexed. */
  page: number;
  pageSize: number;
  orderBy?: ServiceProviderSortColumn;
  orderDirection?: 'asc' | 'desc';
  filters?: ServiceProviderFilters;
}

// The combined status is `invite_status` while the provider is active and has
// been invited, else `status`. Mirrors `combined_status/2` in
// NursingHomeServiceProviderJSON.
const addCombinedStatus = (
  filter: Filter,
  combinedStatus: ServiceProviderStatusCombined | undefined
): void => {
  if (!combinedStatus) {
    return;
  }
  filter.field('status', '==', 'active');
  if (combinedStatus === ServiceProviderStatusCombined.Active) {
    filter.field('invite_status', 'empty', 'true');
  } else {
    filter.field('invite_status', '==', combinedStatus);
  }
};

interface PhoenixServiceProviderResponse {
  id: string;
  name: string;
  alternate_names: string[];
  email: string;
  global_service_provider_id: string | null;
  combined_status: ServiceProviderStatusCombined;
  receipt_type: {
    id: string;
    name: ReceiptTypeName;
  };
}

interface PhoenixPaginatedResponse {
  data: PhoenixServiceProviderResponse[];
  meta: {
    page_size: number;
    current_page: number;
    total_pages: number;
    total_count: number;
  };
}

export interface IServiceProviderPhoenixApi extends Api {
  addServiceProvider(params: {
    nursingHomeId: string;
    data: PostServiceProviderDto;
  }): Promise<{ id: string }>;
  inviteServiceProvider(params: {
    serviceProviderId: string;
    email: string;
  }): Promise<{ success: boolean }>;
  revokeServiceProviderInvite(params: {
    serviceProviderId: string;
  }): Promise<{ data: ServiceProviderWithStatementDescriptorDto }>;
  getServiceProvider(
    serviceProviderId: string
  ): Promise<{ data: ServiceProviderWithStatementDescriptorDto }>;
  updateServiceProviderStatementDescriptor(params: {
    serviceProviderId: string;
    data: { statement_descriptor: string };
  }): Promise<{ success: boolean }>;
  listServiceProviders(
    params: ListServiceProvidersParams
  ): Promise<PaginatedResultSet<ServiceProviderListDto>>;
}

export class ServiceProviderPhoenixApi implements IServiceProviderPhoenixApi {
  private readonly baseUrl: string;

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);
  }

  // eslint-disable-next-line class-methods-use-this
  get headers(): Headers {
    return new Headers({
      'content-type': 'application/json',
    });
  }

  async addServiceProvider({
    nursingHomeId,
    data,
  }: {
    nursingHomeId: string;
    data: PostServiceProviderDto;
  }): Promise<{ id: string }> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/nursing-home/${nursingHomeId}/service-provider/`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: this.headers,
      }
    );
    return result.json();
  }

  async inviteServiceProvider({
    serviceProviderId,
    email,
  }: {
    serviceProviderId: string;
    email: string;
  }): Promise<{ success: boolean }> {
    const data = { data: { email } };
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}/invite`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: this.headers,
      }
    );
    return result.json();
  }

  async revokeServiceProviderInvite({
    serviceProviderId,
  }: {
    serviceProviderId: string;
  }): Promise<{ data: ServiceProviderWithStatementDescriptorDto }> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}/revoke`,
      {
        method: 'POST',
        headers: this.headers,
      }
    );
    return result.json();
  }

  async getServiceProvider(
    serviceProviderId: string
  ): Promise<{ data: ServiceProviderWithStatementDescriptorDto }> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}`
    );
    return result.json();
  }

  async updateServiceProviderStatementDescriptor({
    serviceProviderId,
    data,
  }: {
    serviceProviderId: string;
    data: { statement_descriptor: string };
  }): Promise<{ success: boolean }> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ data }),
        headers: this.headers,
      }
    );
    return result.json();
  }

  async listServiceProviders({
    nursingHomeId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filters,
  }: ListServiceProvidersParams): Promise<
    PaginatedResultSet<ServiceProviderListDto>
  > {
    const filter = new Filter()
      .buildScope({ nursingHomeId })
      .fieldIfPresent('receipt_type_id', '==', filters?.receiptTypeId)
      .fieldIfPresent('name', 'ilike_and', filters?.name);
    addCombinedStatus(filter, filters?.combinedStatus);

    const params = filter.build();
    params.append('page', (page + 1).toString()); // Convert 0-based to 1-based
    params.append('page_size', pageSize.toString());
    if (orderBy) {
      // Names are unique per nursing home, so they keep the order of equal
      // emails stable across pages.
      const columns = orderBy === 'name' ? ['name'] : [orderBy, 'name'];
      for (const column of columns) {
        params.append('order_by[]', column);
        params.append('order_directions[]', orderDirection ?? 'asc');
      }
    }

    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider?${params.toString()}`
    );

    const json = (await result.json()) as PhoenixPaginatedResponse;

    // Map snake_case to camelCase
    const data = json.data.map((sp) => ({
      id: sp.id,
      name: sp.name,
      alternateNames: sp.alternate_names || [],
      email: sp.email,
      globalServiceProviderId: sp.global_service_provider_id ?? undefined,
      combinedStatus: sp.combined_status,
      receiptType: {
        id: sp.receipt_type.id,
        name: sp.receipt_type.name,
      },
    }));

    return new PaginatedResultSet(
      data,
      json.meta.total_count,
      json.meta.page_size,
      json.meta.current_page - 1 // Convert back to 0-based
    );
  }
}
